import { useCallback, useState } from "react";

/** The base map under everything else. "none" = a plain background, data only. */
export type Basemap = "street" | "satellite" | "none";

export type BoundaryKind = "zone" | "ward" | "block";

/**
 * What the project map shows: which layers are on, which names are drawn, the
 * base map and the analysis modes. Held by the page (not inside MapPreview) so
 * the Filters & Layers side panel and the map read and change the same state.
 */
export type MapView = {
  /** Layer switches: imagery, zone, ward, block, features (field data), labels (property numbers). */
  visible: Record<string, boolean>;
  /** Name labels per boundary level. */
  boundaryLabels: Record<BoundaryKind, boolean>;
  basemap: Basemap;
  /** Colour wards and blocks by survey progress instead of by level. */
  highlightProgress: boolean;
  /** Property numbers on editable-layer shapes. */
  editableLabels: boolean;
  /** Plot numbers on editable-layer shapes, switched separately. */
  editablePlotLabels: boolean;
  /** Draw only the outline, with no fill: field data and editable layers each. */
  outlineOnly: { features: boolean; editable: boolean };
};

const BASEMAP_KEY = "gs-map-basemap";
/** The earlier on/off "Satellite map" switch, read once so the choice carries over. */
const OLD_SATELLITE_KEY = "gs-map-satellite";

/**
 * The panel's three Base map tiles, one picked at a time: Street, Satellite,
 * or Drone (the drone imagery alone, on a plain background).
 */
export type BaseChoice = "street" | "satellite" | "drone";

function readBaseChoice(): BaseChoice {
  try {
    const v = localStorage.getItem(BASEMAP_KEY);
    if (v === "street" || v === "satellite" || v === "drone") return v;
    // "none" was the plain background before Drone became a tile of its own.
    if (v === "none") return "street";
    return localStorage.getItem(OLD_SATELLITE_KEY) === "1" ? "satellite" : "street";
  } catch {
    return "street";
  }
}

function initialView(): MapView {
  const choice = readBaseChoice();
  return {
    visible: { imagery: choice === "drone", zone: true, ward: true, block: true, features: true, labels: true },
    boundaryLabels: { zone: false, ward: true, block: true },
    basemap: choice === "drone" ? "none" : choice,
    highlightProgress: false,
    editableLabels: true,
    editablePlotLabels: true,
    outlineOnly: { features: false, editable: false },
  };
}

export type MapViewState = ReturnType<typeof useMapView>;

export function useMapView() {
  const [view, setView] = useState<MapView>(initialView);

  const toggleLayer = useCallback((key: string) => {
    setView((v) => ({ ...v, visible: { ...v.visible, [key]: !v.visible[key] } }));
  }, []);

  /** Shows all three boundary levels, or hides them all when any is showing. */
  const toggleAllBoundaries = useCallback(() => {
    setView((v) => {
      const any = v.visible.zone || v.visible.ward || v.visible.block;
      return { ...v, visible: { ...v.visible, zone: !any, ward: !any, block: !any } };
    });
  }, []);

  const toggleBoundaryLabels = useCallback((kind: BoundaryKind) => {
    setView((v) => ({ ...v, boundaryLabels: { ...v.boundaryLabels, [kind]: !v.boundaryLabels[kind] } }));
  }, []);

  const setBasemap = useCallback((basemap: Basemap) => {
    try {
      localStorage.setItem(BASEMAP_KEY, basemap);
    } catch {
      // blocked storage: the choice still applies, it just isn't remembered
    }
    setView((v) => ({ ...v, basemap }));
  }, []);

  /** Picks one Base map tile: the street or satellite map with the drone
   *  imagery off, or the drone imagery alone. Remembered like setBasemap. */
  const chooseBase = useCallback((choice: BaseChoice) => {
    try {
      localStorage.setItem(BASEMAP_KEY, choice);
    } catch {
      // blocked storage: the choice still applies, it just isn't remembered
    }
    setView((v) => ({
      ...v,
      basemap: choice === "drone" ? "none" : choice,
      visible: { ...v.visible, imagery: choice === "drone" },
    }));
  }, []);

  const toggleHighlightProgress = useCallback(() => {
    setView((v) => ({ ...v, highlightProgress: !v.highlightProgress }));
  }, []);

  const toggleEditableLabels = useCallback(() => {
    setView((v) => ({ ...v, editableLabels: !v.editableLabels }));
  }, []);

  const toggleEditablePlotLabels = useCallback(() => {
    setView((v) => ({ ...v, editablePlotLabels: !v.editablePlotLabels }));
  }, []);

  const toggleOutlineOnly = useCallback((kind: "features" | "editable") => {
    setView((v) => ({ ...v, outlineOnly: { ...v.outlineOnly, [kind]: !v.outlineOnly[kind] } }));
  }, []);

  return {
    view,
    toggleLayer,
    toggleAllBoundaries,
    toggleBoundaryLabels,
    setBasemap,
    chooseBase,
    toggleHighlightProgress,
    toggleEditableLabels,
    toggleEditablePlotLabels,
    toggleOutlineOnly,
  };
}
